import { defineStore, storeToRefs } from 'pinia';
import { ref } from 'vue';
import type { Appliance } from '../api-client-shop';
import { useDataStore } from './data';

export interface OrderPosition {
    'articleNumber'?: string | null;
    'articleName'?: string | null;
    'qty'?: number;
}

export type ExtendedOrderPosition = OrderPosition & {
  price?: number;
  currency?: string;
  stock?: string;
  stockType?: string;
  stockText?: string;
};

export const useOrderStore = defineStore('orderStore', () => {
  //-------State------------------------------------------------
  const orders = ref<Array<ExtendedOrderPosition>>([]);
  const dataStore = useDataStore();

  //-------MUTATIONS------------------------------------------------------
  const clearOrders = () => {
    orders.value = [];
  };

  //-------ACTIONS----------------------------------------------------------
  const addArticle = (
    position: OrderPosition,
    articlePosition: number | undefined | null
  ) => {
    //--------VIRTUAL GROUP--------
    const { getZPartsByPosition, getVirtualElementsWithoutGroups } = dataStore;

    if (!!articlePosition) {
      const positionsByVirtualGroup =
        getVirtualElementsWithoutGroups(articlePosition);

      if (positionsByVirtualGroup.length > 0) {
        positionsByVirtualGroup.forEach((article) => {
          //check is in cart
          const virtualPosition = orders.value.find(
            (x) => x.articleNumber === article.articleNumber
          );

          if (virtualPosition !== undefined) {
            virtualPosition.qty =
              (virtualPosition.qty || 1) +
              (position?.qty || 1) * (article.quantity || 1);
          } else {
            orders.value.push({
              articleName: article.name,
              articleNumber: article.articleNumber,
              qty: (article.quantity || 1) * (position?.qty || 1),
              price: article.priceGross || 0,
              currency: article.currency,
              stockText: article.stockText,
              stockType: article.stockType
            } as ExtendedOrderPosition);
          }
        });

        return addArticleToCart();
      }
    }

    //----------------NORMAL ARTICLE AND Z-PARTS----------------------------------
    //check is in cart
    const orderPosition = orders.value.find(
      (x) => x.articleNumber === position.articleNumber
    );
    if (orderPosition !== undefined) {
      orderPosition.qty = (orderPosition.qty || 1) + (position.qty || 1);
    } else {
      orders.value.push(position);
    }

    //Check z-part
    if (!!articlePosition) {
      const zParts = getZPartsByPosition(articlePosition).value;

      zParts.forEach((zPart) => {
        //check ist in cart
        const zPartPosition = orders.value.find(
          (x) => x.articleNumber === zPart.articleNumber
        );
        if (zPartPosition !== undefined) {
          zPartPosition.qty =
            (zPartPosition.qty || 1) +
            (position?.qty || 1) * (zPart.quantity || 1);
        } else {
          orders.value.push({
            articleName: zPart.name,
            articleNumber: zPart.articleNumber,
            qty: (zPart.quantity || 1) * (position?.qty || 1),
            price: zPart.priceGross || 0,
            currency: zPart.currency,
            stockText: zPart.stockText,
            stockType: zPart.stockType
          } as ExtendedOrderPosition);
        }
      });

      return addArticleToCart();
    }

    return addArticleToCart();
  };

  const addArticleToCart = async () => {
    const { serialnumber } = storeToRefs(dataStore);

    for (const position of orders.value) {
      if (!position.articleNumber) continue;

      await window.lh.hau.cart.addItemV2(
        position.articleNumber || '',
        position.qty || 1,
        'ERT',
        { serial_number: serialnumber.value } as Appliance
      ).then(() => {
        window.lh.hau.cart.updateCartBadge();
      });
    }

    clearOrders();
  };

  //---------MUTATATIONS------------------------------------------------------

  //--------------------Getters----------------------------------------------

  return {
    orders,
    addArticle
  };
});
